import { getTimeParams } from '@components/destinations/destination-view/events/utils';
import { ISourceStore } from '@stores/source';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { BATCH_SIZE, isTableLoading } from './utils';

export interface EventStatParams {
  start: string;
  aggregationMinutes: number;
}

export interface TrackEventParams {
  start: string;
  searchBy: string;
  sortBy: string;
  sortOrder: string;
  page: number;
  end: string;
}

export const SORT_ORDER = [
  {
    value: 'ASC',
    label: 'Asc',
  },
  {
    value: 'DESC',
    label: 'Desc',
  },
];

export const SORT_OPTIONS = [
  {
    value: 'name',
    label: 'Sort by Name',
  },
  {
    value: 'count',
    label: 'Sort by Count',
  },
  {
    value: 'lastHappened',
    label: 'Sort by Last Happened',
  },
];

export const SOURCE_TIME_FILTERS = [
  {
    value: 'day',
    label: 'Past 1 day',
  },
  {
    value: 'week',
    label: 'Past 7 days',
  },
  {
    value: 'month',
    label: 'Past 30 days',
  },
];

export default (source: ISourceStore) => {
  const [timeFilter, setTimeFilter] = useState(SOURCE_TIME_FILTERS[0].value);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[1].value);
  const [sortOrder, setSortOrder] = useState(SORT_ORDER[1].value);
  const [search, setSearch] = useState('');
  const [now, setNow] = useState(moment.utc().toISOString());
  const [page, setPage] = useState(1);

  const getEventStats = () => {
    setLoading(true);
    source.getEventStats?.(getTimeParams(timeFilter), () => setLoading(false));
  };

  const getTrackEvents = (pageNo: number, end = now) =>
    source.getTrackEvents?.({
      start: getTimeParams(timeFilter).start,
      searchBy: search,
      sortBy,
      sortOrder,
      page: pageNo,
      end,
    });

  useEffect(() => {
    getEventStats();
  }, [timeFilter]);

  useEffect(() => {
    const current = moment.utc().toISOString();
    setPage(1);
    getTrackEvents(0, current);
    setNow(current);
  }, [timeFilter, sortBy, sortOrder, search]);

  const onRefresh = () => {
    const current = moment.utc().toISOString();
    setPage(1);
    getEventStats();
    getTrackEvents(0, current);
    setNow(current);
  };

  const onPageChange = (newPage: number, perPage: number) => {
    const totalEvents = source.trackEvents?.data?.length || 0;
    if (totalEvents && totalEvents % 100 === 0 && totalEvents === (newPage - 1) * perPage) {
      getTrackEvents(totalEvents / BATCH_SIZE);
    }
  };

  return {
    onRefresh,
    timeFilter,
    setTimeFilter,
    loading,
    eventStats: source.eventStats,
    trackEvents: source.trackEvents || { hasMore: false },
    trackFilters: {
      sortBy,
      sortOrder,
      search,
    },
    setTrackFilters: {
      setSortBy,
      setSortOrder,
      setSearch,
      setPage,
    },
    page,
    onPageChange,
    isTableLoading: isTableLoading(
      source.trackEvents?.data,
      page,
      source.trackEvents?.hasMore || false,
    ),
  };
};
